import _ from 'lodash';

import { hasQueryOverrides } from 'components/V5Gadget/seriesDataKey';

import {
  ChartDefinitionResult,
  DATE_DIMENSION,
  GROUP_LIMIT,
} from './chartDefinitionParts';
import { isLegacyMatrixTable } from './buildLegacyMatrixDefinition';
import { isFieldColumn } from './fieldColumns';
import {
  getDimensionKind,
  hasTermDimension,
  isTermKind,
  toDimension,
} from './dimensions';

const DEFAULT_ROW_INTERVAL: FleetOps.Interval = 'month';
const DEFAULT_COLUMN_INTERVAL: FleetOps.Interval = 'week';

const compact = <T extends object>(value: T): T =>
  _.omitBy(value, _.isUndefined) as T;

// What a Table's layout lets it show. Options outside these are kept on the
// block (so switching back restores them) but left out of the chart.
export const getTableCapabilities = (
  content: Pick<
    Studio.ChartContent,
    'dimensions' | 'dimensionOptions' | 'table'
  >,
) => {
  const options = content.table ?? {};
  const columns = options.columns ?? 'metrics';
  const [, dimB] = content.dimensions ?? [];
  const hasDimension = (content.dimensions ?? []).length > 0;
  // Ranges, days and months group rows without terms to limit or rank
  const hasTermRows = hasTermDimension(content);
  const isReversed = !!options.reverseAxes;
  const isTwoDimensional = columns !== 'metrics';
  // Unless it's the columns, a second dimension of terms splits each row of
  // terms into a row per pair (the classic matrix's secondaryDimensionA)
  const hasSecondaryRows =
    columns !== 'dimension' &&
    !!dimB &&
    hasTermRows &&
    !isReversed &&
    isTermKind(getDimensionKind((content.dimensionOptions ?? {})[dimB]));
  return {
    columns,
    isTwoDimensional,
    hasSecondaryRows,
    // Rows by date when there's no dimension to group by
    isDateRows: !hasDimension,
    usesInterval: !hasDimension || columns === 'dates',
    // A 2D matrix only has deltas along date columns, and reversed axes
    // have none
    canCompare: !isReversed && columns !== 'dimension',
    // A 2D date matrix only compares each interval with the one before
    canCompareLastYear: !isReversed && columns === 'metrics',
    canShowComparisonValue: !isReversed && !isTwoDimensional,
    // Rows of pairs aren't ranked
    canRank:
      !isReversed &&
      !isTwoDimensional &&
      !hasSecondaryRows &&
      (!hasDimension || hasTermRows),
    // Threshold colours, as the classic matrix offers them: not reversed
    canColourCells: !isReversed,
    // Top N, show all or exclude top N apply to grouped terms; exclude top
    // N needs a single dimension
    canLimit: hasTermRows,
    canExcludeTop: hasTermRows && !isTwoDimensional,
  };
};

// A Table of metric totals, a column per metric (unless 2-D), rather than
// one with rows of its own kind (records, date comparison, pace, reminders,
// entity details, metric list or KPI list). Where rows can't be other than
// totals, its rows setting is ignored.
export const isTotalsTable = ({
  content,
  canListRecords,
}: {
  content: Pick<Studio.ChartContent, 'visType' | 'table'>;
  canListRecords: boolean;
}): boolean =>
  content.visType === 'table' &&
  (!canListRecords || (content.table?.rows ?? 'totals') === 'totals');

// The comparison a Table renders with, if its layout can show one
export const getTableComparison = (
  content: Pick<
    Studio.ChartContent,
    'dimensions' | 'dimensionOptions' | 'table'
  >,
): PersistedComparisonType | undefined => {
  const comparison = content.table?.comparison;
  const { canCompare, canCompareLastYear } = getTableCapabilities(content);
  // Following the report's, it saves none of its own
  if (!comparison || !canCompare || comparison.compareType === 'report') {
    return undefined;
  }
  return {
    compareType:
      comparison.compareType === 'lastYear' && canCompareLastYear
        ? 'lastYear'
        : 'previous',
  };
};

// What a metric of a Table following the report's comparison shows,
// without a cell type of its own
export const DEFAULT_REPORT_CELL_TYPE = 'percentDelta' as const;

// A Table showing totals (not records, nor a date comparison or pace
// table, which have a baseline instead) that follows the report's
// comparison, in a layout that can show one
export const isTableFollowingReport = (
  content: Pick<
    Studio.ChartContent,
    'visType' | 'dimensions' | 'dimensionOptions' | 'table'
  >,
): boolean =>
  content.visType === 'table' &&
  content.table?.comparison?.compareType === 'report' &&
  !isRecordsTable(content) &&
  !isLegacyMatrixTable(content) &&
  getTableCapabilities(content).canCompare;

// What a metric's comparison cells show: its cell type, else (following
// the report's comparison) the % change. Undefined is none.
export const getMetricCellType = ({
  cellType,
  followsReport,
}: {
  cellType: Studio.TableMetricOptions['cellType'];
  followsReport: boolean;
}): MatrixCellType | undefined =>
  cellType === 'none'
    ? undefined
    : cellType ?? (followsReport ? DEFAULT_REPORT_CELL_TYPE : undefined);

const cellTypeIncludes = ({
  cellType,
  comparison,
}: {
  cellType: MatrixCellType | undefined;
  comparison: 'delta' | 'percentDelta';
}) => cellType === comparison || cellType === 'both';

// Whether a Table lists a dataset's records rather than totalling metrics
export const isRecordsTable = (
  content: Pick<Studio.ChartContent, 'visType' | 'table'>,
): boolean => content.visType === 'table' && content.table?.rows === 'records';

// A records Table renders as the classic simple grid: the dataset's records,
// a column per field. It takes no metrics, so its series are empty.
export const buildRecordsDefinition = ({
  card,
  name,
}: {
  card: Studio.ChartContent;
  name: string;
}): ChartDefinitionResult => {
  const options = card.table ?? {};
  const records = options.records ?? {};
  const columns = records.columns ?? [];
  if (!records.dataType) {
    return { missing: 'Choose a dataset to list its records' };
  }
  if (columns.length === 0) {
    return { missing: 'Add a column to list records' };
  }
  return {
    chartDefinition: compact({
      id: card.id,
      name,
      description: card.description,
      gadgetType: 'simpleGrid',
      series: [],
      simpleGridConfig: compact({
        dataType: records.dataType,
        fields: columns.map((c) => c.field),
        fieldAliases: columns,
        sort: records.sort ?? [],
        disableLinks: records.isLinksDisabled || undefined,
        gridSizing: options.gridSizing,
      }),
    }),
  };
};

// A Table renders as a V5 matrix. Rows are the first dimension (or dates),
// columns a metric each, the intervals of one metric ('dates', as the old
// single metric date matrix) or the terms of the second dimension. Unless
// they're the columns, the second dimension's terms split the rows: a row per
// pair of terms. Without options this builds the plain table (every term, by
// the first metric, or by month), and every option is only written when it
// changes the chart, so tables saved before options existed still rebuild
// exactly. A field column (see fieldColumns) only shows each row's value.
const buildTableDefinition = ({
  card,
  base,
}: {
  card: Studio.ChartContent;
  base: V5ChartDefinition;
}): ChartDefinitionResult => {
  const options = card.table ?? {};
  const [dimA, dimB] = card.dimensions ?? [];
  const capabilities = getTableCapabilities(card);
  const { columns, isTwoDimensional } = capabilities;

  if (columns === 'dates' && !dimA) {
    return { missing: 'Add a dimension to show dates as columns' };
  }
  if (columns === 'dimension' && !dimB) {
    return { missing: 'Add a second dimension to show it as columns' };
  }

  // Following the report's comparison, the cells show it when the report
  // has one, as a classic report gadget's do; its metrics without a cell
  // type show the % change
  const followsReport = isTableFollowingReport(card);
  const hasComparison = followsReport || !!getTableComparison(card);
  // Each column's options are keyed by its entry in metricIds (a metric, or
  // a variant of one: see seriesVariants), as base's series are built
  const keys = card.metricIds ?? [];
  const getColumnOptions = ({
    s,
    index,
  }: {
    s: V5ChartDefinitionSeries;
    index: number;
  }) => (options.metrics ?? {})[keys[index] ?? s.metricId];
  // Two-dimensional tables show one metric
  const series = (isTwoDimensional ? base.series.slice(0, 1) : base.series).map(
    (s, index): V5ChartDefinitionSeries => {
      const m = getColumnOptions({ s, index });
      // A column asking its own question has no comparison, rank or share
      // of total (as a chart's series has no comparison)
      const isOwnQuery = hasQueryOverrides(s.queryOverrides);
      // Text has no total, change, rank or colour
      if (isFieldColumn(m)) {
        return compact({
          ...s,
          displayName: m?.displayName || undefined,
          isTotalEnabled: false,
        });
      }
      if (!m) {
        return followsReport && !isOwnQuery
          ? { ...s, matrixCellType: DEFAULT_REPORT_CELL_TYPE }
          : s;
      }
      const cellType =
        hasComparison && !isOwnQuery
          ? getMetricCellType({ cellType: m.cellType, followsReport })
          : undefined;
      const canRank = capabilities.canRank && !isOwnQuery;
      const isPercentageOfTotalEnabled =
        canRank && !!m.isPercentageOfTotalEnabled;
      return compact({
        ...s,
        displayName: m.displayName || undefined,
        isTotalEnabled: m.isTotalHidden ? false : undefined,
        isStatusFlag: m.isStatusFlag || undefined,
        matrixCellType: cellType,
        matrixShowComparisonValue:
          (!!cellType &&
            capabilities.canShowComparisonValue &&
            m.showComparisonValue) ||
          undefined,
        isRankingEnabled: (canRank && m.isRankEnabled) || undefined,
        isPercentageOfTotalEnabled: isPercentageOfTotalEnabled || undefined,
        percentageOfTotalPrecision: isPercentageOfTotalEnabled
          ? m.percentageOfTotalPrecision
          : undefined,
        // A status flag is coloured as a flag
        cellColourConfig:
          capabilities.canColourCells && !m.isStatusFlag
            ? m.cellColourConfig
            : undefined,
      });
    },
  );
  // The table's own metrics pick and sort its rows (see
  // MatrixOverrideGroup): its first, by default. Field columns don't.
  const ownSeries = series.filter(
    (s, index) =>
      !hasQueryOverrides(s.queryOverrides) &&
      !isFieldColumn(getColumnOptions({ s, index })),
  );
  const [primary] = ownSeries.length > 0 ? ownSeries : series;

  const dimensionOptions = card.dimensionOptions ?? {};
  const rows: Partial<V5ChartDefinition> = dimA
    ? {
        dimensionA: toDimension({
          field: dimA,
          options: dimensionOptions[dimA],
        }),
        secondaryDimensionA: capabilities.hasSecondaryRows
          ? toDimension({ field: dimB, options: dimensionOptions[dimB] })
          : undefined,
        isSecondaryRowsStacked:
          (capabilities.hasSecondaryRows && options.isSecondaryRowsStacked) ||
          undefined,
      }
    : {
        dimensionA: DATE_DIMENSION,
        trendByCalendarInterval: options.interval ?? DEFAULT_ROW_INTERVAL,
      };
  const cols = ((): Partial<V5ChartDefinition> => {
    if (columns === 'dates') {
      return {
        dimensionB: DATE_DIMENSION,
        trendByCalendarInterval: options.interval ?? DEFAULT_COLUMN_INTERVAL,
      };
    }
    if (columns === 'dimension') {
      return {
        dimensionB: toDimension({
          field: dimB,
          options: dimensionOptions[dimB],
        }),
      };
    }
    return {};
  })();

  // Grouped rows default to the first metric, descending; date rows to
  // their own order
  const sort = ((): SortConfig | undefined => {
    const { sort: chosen } = options;
    if (!chosen) {
      return dimA
        ? {
            field: primary.metricId,
            metricId: primary.metricId,
            direction: 'desc',
          }
        : undefined;
    }
    if (chosen.by === 'dimension') {
      return {
        field: dimA ?? DATE_DIMENSION.field,
        direction: chosen.direction,
      };
    }
    const sorted =
      ownSeries.find((s) => s.metricId === chosen.metricId) ?? primary;
    // Only 1D tables have a change column per metric to sort by
    if (
      chosen.comparison &&
      !isTwoDimensional &&
      cellTypeIncludes({
        cellType: sorted.matrixCellType,
        comparison: chosen.comparison,
      })
    ) {
      return compact({
        field: `${sorted.metricId}-${chosen.comparison}`,
        metricId: sorted.metricId,
        isComparison: true,
        isAbsolute: chosen.isAbsolute || undefined,
        direction: chosen.direction,
      });
    }
    return {
      field: sorted.metricId,
      metricId: sorted.metricId,
      direction: chosen.direction,
    };
  })();

  const limit = ((): Partial<V5ChartDefinition> => {
    if (!capabilities.canLimit) {
      return {};
    }
    const chosen = options.limit ?? { mode: 'all' };
    const n = chosen.n ?? GROUP_LIMIT;
    if (chosen.mode === 'all') {
      return { groupByLimitMode: 'show all' };
    }
    if (chosen.mode === 'excludeTop' && capabilities.canExcludeTop) {
      // Excludes the top N by its own metric (one still in the Table), else
      // by the sorted (or first) metric, highest first. The rest are shown
      // in the Table's sort.
      const own =
        chosen.excludeBy &&
        ownSeries.some((s) => s.metricId === chosen.excludeBy?.metricId)
          ? chosen.excludeBy
          : undefined;
      const excludeBy =
        own?.metricId ??
        (sort && sort.metricId ? sort.metricId : primary.metricId);
      return {
        groupByLimitMode: compact({
          type: 'exclude top n' as const,
          n,
          excludeSort: {
            field: excludeBy,
            metricId: excludeBy,
            direction: own?.direction ?? ('desc' as const),
          },
          displaySort: sort,
        }),
      };
    }
    return { groupByLimit: n, groupByLimitMode: 'top n' };
  })();

  return {
    chartDefinition: compact({
      ...base,
      series,
      ...rows,
      ...cols,
      ...limit,
      groupBySortBy: dimA && sort ? sort.direction : undefined,
      grouping: sort ? { defaultSort: sort } : undefined,
      excludePartialIntervals:
        (capabilities.usesInterval && options.excludePartialIntervals) ||
        undefined,
      gridSizing: options.gridSizing,
      reverseAxes: options.reverseAxes || undefined,
      totalsColumnPosition: options.reverseAxes
        ? options.totalsColumnPosition
        : undefined,
    }),
  };
};

export default buildTableDefinition;
